import { currencies } from '@/services/currencies';

import request from '@/services/request';
import { GATEWAYS, PAYMENT_METHODS, checkShouldSkipAdditionalDetails } from '@/utils';
import { isGoalMeterActive } from '@/utils/goal-meter';

export const state = {
  name: '',
  settings: {},
  active: true,
  default: false,
  id: null,
  slug: '',
  currency: null,
  chargeTotal: null,
  donorCount: null,
  matchingEnabled: false,
  matchingRatio: 1,
  matchingDescription: null
};

export const mutations = {
  SET_CAMPAIGN(state, newValue) {
    state.name = newValue.name;
    state.settings = newValue.checkoutSettings;
    state.active = newValue.active;
    state.default = newValue.default;
    state.id = newValue.id;
    state.slug = newValue.slug;
    state.chargeTotal = newValue.chargeTotal;
    state.donorCount = newValue.donorCount;
    state.currency = newValue.currency;
    state.matchingEnabled = newValue.matchingEnabled;
    state.matchingRatio = newValue.matchingRatio;
    state.matchingDescription = newValue.matchingDescription;
  },
  RESET_CAMPAIGN(state) {
    state.name = '';
    state.settings = {};
    state.active = true;
    state.default = false;
    state.id = null;
    state.slug = '';
    state.matchingEnabled = false;
    state.matchingRatio = 1;
    state.matchingDescription = null;
  }
};

export const getters = {
  campaignLoaded(state) {
    return !!state.name;
  },
  matchingSettings(state) {
    return {
      matchingEnabled: state.matchingEnabled,
      matchingRatio: state.matchingRatio,
      matchingDescription: state.matchingDescription
    };
  },
  shouldSkipAdditionalDetails(state, getters, rootState) {
    return checkShouldSkipAdditionalDetails(state.settings || {}, rootState.donation);
  },
  hasActiveGoalMeter(state) {
    return isGoalMeterActive(state.settings?.goalMeter);
  },
  isOnlyDafEnabled(state) {
    // Not best approach
    const disabledPaymentMethods = state.settings.disablePaymentMethods;
    return (
      disabledPaymentMethods?.[PAYMENT_METHODS.ACH] &&
      disabledPaymentMethods?.[PAYMENT_METHODS.CARD] &&
      disabledPaymentMethods?.appleGooglePay &&
      disabledPaymentMethods?.[GATEWAYS.PAYPAL]
    );
  }
};

export const actions = {
  async fetchCampaign({ state, commit, dispatch, rootState }, { campaignSlug, withGatewayIntegrations = false }) {
    try {
      const { data, error } = await request.get(`/public/campaign/${rootState.orgId}/${campaignSlug}?gatewayIntegrations=${withGatewayIntegrations}`);
      if (error) {
        throw new Error(data);
      }
      commit('SET_CAMPAIGN', data);

      commit('donation/SET_PAY_COVER_FEES', data.checkoutSettings.coverFeesChecked, { root: true });

      // Re-validate currency for this specific campaign
      // This handles the case where user navigates directly to a campaign with different currency settings than the default campaign
      const currentCurrency = rootState.donation.currency;
      const campaignDisabledCurrencies = data.checkoutSettings?.disabledCurrencies || [];
      const savedCurrency = localStorage.getItem('Double.selectedCurrency');
      const ipBasedCurrency = rootState.publicOrgSettings.ipBasedCurrency;
      const programmaticCurrency = rootState.donation.programmaticCurrency;

      let currencyToUse = currentCurrency;

      // Priority: 1. Programmatic currency (Double.openCheckout), 2. User's saved preference, 3. IP-based currency, 4. Current currency, 5. First enabled currency
      if (programmaticCurrency && !campaignDisabledCurrencies.includes(programmaticCurrency)) {
        // Programmatic currency has highest precedence
        currencyToUse = programmaticCurrency;
      } else if (savedCurrency && !campaignDisabledCurrencies.includes(savedCurrency)) {
        // User's saved preference is valid for this campaign
        currencyToUse = savedCurrency;
      } else if (ipBasedCurrency && !campaignDisabledCurrencies.includes(ipBasedCurrency) && currentCurrency !== ipBasedCurrency) {
        // IP-based currency is valid for this campaign but wasn't used before (was disabled in default campaign)
        currencyToUse = ipBasedCurrency;
      } else if (campaignDisabledCurrencies.includes(currentCurrency)) {
        // Current currency is disabled in this campaign - find first enabled currency
        currencyToUse = currencies.value.find((currency) => !campaignDisabledCurrencies.includes(currency.value))?.value || 'usd';
      }

      // Only update if currency needs to change
      if (currencyToUse !== currentCurrency) {
        dispatch('donation/SET_CURRENCY_ACTIONS', currencyToUse, { root: true });
      }

      if (!rootState.donation.frequency) {
        dispatch('donation/SET_FREQUENCY_ACTIONS', data.checkoutSettings?.frequencyDefaultComputed || 'single', { root: true });
      }

      const customQuestions = state.settings.customQuestions
        .map((customQuestion) => ({
          ...customQuestion,
          answer: customQuestion.type === 'checkbox' && customQuestion.checkedByDefault ? true : ''
        }))
        .sort((a, b) => a.order - b.order);

      commit('donation/SET_CUSTOM_QUESTIONS', customQuestions, { root: true });

      // We override the gateways with the campaign gateways if given
      if (data.gateways?.length) {
        commit('publicOrgSettings/SET_GATEWAYS', data.gateways, { root: true });
      }
      // call again SET_CURRENCY_ACTIONS to update the CC gateway
      dispatch('donation/SET_CURRENCY_ACTIONS', rootState.donation.currency, { root: true });

      return data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }
};
